import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { DISABLED_APY_BIGINT, getUnixTs, Result, TIME } from "@bridgesplit/utils";

import { AbfLoanExpanded, CapType, LendingVaultExpanded, LtvInfo, StrategyExpandedInfo } from "../types";
import { getVaultNetAssetValue, getZcLedger } from "../api";

export function serializeStrategyDuration(strategyDuration: StrategyDuration) {
    const duration = strategyDuration.duration;
    const durationType = strategyDuration.durationType;
    return JSON.stringify({ duration, durationType });
}

export function deserializeStrategyDuration(serializedStrategyDuration: string): Result<StrategyDuration> {
    const strategyDuration = JSON.parse(serializedStrategyDuration) as StrategyDuration;
    if (strategyDuration.duration === undefined || strategyDuration.durationType === undefined) {
        return Result.errFromMessage("Invalid duration");
    }
    return Result.ok(strategyDuration);
}

export function deserializeStrategyDurationOrUndefined(
    serializedStrategyDuration: string
): StrategyDuration | undefined {
    return deserializeStrategyDuration(serializedStrategyDuration).unwrapOrUndefined();
}

export function isStrategyDurationSame(a: StrategyDuration | undefined, b: StrategyDuration | undefined) {
    if (!a || !b) return false;

    return serializeStrategyDuration(a) === serializeStrategyDuration(b);
}
interface MaxAmountFromCap {
    current1hrAmountRemaining: bigint;
    current24hrAmountRemaining: bigint;
    currentGlobalAmountRemaining: bigint;
    maxBalanceRemaining: bigint;
}

export function getVaultMaxAmountFromCap(
    vaultExpanded: LendingVaultExpanded | undefined,
    type: CapType
): MaxAmountFromCap | undefined {
    if (!vaultExpanded) {
        return undefined;
    }

    const { principalCaps, capMonitor } = (() => {
        switch (type) {
            case CapType.Supply:
                return {
                    principalCaps: vaultExpanded.vaultStrategy?.terms.supplyCaps,
                    capMonitor: vaultExpanded.vaultStrategy?.supplyCapMonitor
                };
            case CapType.Borrow:
                return {
                    principalCaps: vaultExpanded.vaultStrategy?.terms.borrowCaps,
                    capMonitor: vaultExpanded.vaultStrategy?.borrowCapMonitor
                };
            case CapType.Withdraw:
                return {
                    principalCaps: vaultExpanded.vaultStrategy?.terms.withdrawCaps,
                    capMonitor: vaultExpanded.vaultStrategy?.withdrawCapMonitor
                };
        }
    })();

    if (!principalCaps || !capMonitor) {
        return undefined;
    }

    const now = getUnixTs();
    let current1hrAmountRemaining: bigint = DISABLED_APY_BIGINT;
    let current24hrAmountRemaining: bigint = DISABLED_APY_BIGINT;
    let currentGlobalAmountRemaining: bigint = DISABLED_APY_BIGINT;

    // Calculate 1hr max
    const oneHourPeriodStart = capMonitor.startTime1hr;
    const oneHourAmount = capMonitor.principal1hr;
    const oneHourCap = principalCaps.max1hr;

    if (oneHourCap !== DISABLED_APY_BIGINT) {
        if (now - oneHourPeriodStart > TIME.HOUR) {
            current1hrAmountRemaining = oneHourCap;
        } else {
            current1hrAmountRemaining = oneHourCap - oneHourAmount;
        }
    }

    // Calculate 24hr max
    const twentyFourHourPeriodStart = capMonitor.startTime24hr;
    const twentyFourHourAmount = capMonitor.principal24hr;
    const twentyFourHourCap = principalCaps.max24hr;

    if (twentyFourHourCap !== DISABLED_APY_BIGINT) {
        if (now - twentyFourHourPeriodStart > TIME.DAY) {
            current24hrAmountRemaining = twentyFourHourCap;
        } else {
            current24hrAmountRemaining = twentyFourHourCap - twentyFourHourAmount;
        }
    }

    // Calculate global max based on cap type
    const maxOutstanding = principalCaps.maxOutstanding;
    if (maxOutstanding !== DISABLED_APY_BIGINT) {
        if (type === CapType.Supply) {
            const navValue = BigInt(Math.floor(getVaultNetAssetValue(vaultExpanded, now) ?? 0));
            const remaining = maxOutstanding - navValue;
            currentGlobalAmountRemaining = remaining > BigInt(0) ? remaining : BigInt(0);
        } else if (type === CapType.Borrow) {
            const deployedAmount = BigInt(Math.floor(vaultExpanded.vaultStrategy?.strategy.currentDeployedAmount ?? 0));
            const remaining = maxOutstanding - deployedAmount;
            currentGlobalAmountRemaining = remaining > BigInt(0) ? remaining : BigInt(0);
        }
    }

    // Return minimum of all non-disabled caps
    const caps: bigint[] = [];
    if (current1hrAmountRemaining !== DISABLED_APY_BIGINT) {
        caps.push(current1hrAmountRemaining);
    }
    if (current24hrAmountRemaining !== DISABLED_APY_BIGINT) {
        caps.push(current24hrAmountRemaining);
    }
    if (currentGlobalAmountRemaining !== DISABLED_APY_BIGINT) {
        caps.push(currentGlobalAmountRemaining);
    }

    let maxBalanceRemaining: bigint;
    if (caps.length === 0) {
        maxBalanceRemaining = DISABLED_APY_BIGINT;
    } else {
        maxBalanceRemaining = caps.reduce((min, val) => (val < min ? val : min), caps[0]);
    }

    return {
        current1hrAmountRemaining,
        current24hrAmountRemaining,
        currentGlobalAmountRemaining,
        maxBalanceRemaining
    };
}

export function findStrategyLtvInfo(
    strategy: StrategyExpandedInfo | undefined,
    collateralMint: string | undefined
): LtvInfo | undefined {
    if (!strategy || !collateralMint) return undefined;

    const collateralTerms = strategy.collateral.find((c) => c.metadata.mint === collateralMint);

    return collateralTerms?.ltvInfo;
}

export function getTargetStrategyMintDurationTerms(
    strategy: StrategyExpandedInfo | undefined,
    loanExpanded: AbfLoanExpanded | undefined,
    targetMint: string | undefined
) {
    const ledger = getZcLedger(loanExpanded);

    if (!strategy || !targetMint || !ledger) return undefined;

    const currentDuration: StrategyDuration = {
        duration: ledger.duration,
        durationType: ledger.durationType
    };

    const targetMintStrategyTerm = strategy.terms.assetTerms[targetMint];
    if (!targetMintStrategyTerm) return undefined;

    const matchingDurationTerms = targetMintStrategyTerm.durationAndApys.find(([duration]) =>
        isStrategyDurationSame(duration, currentDuration)
    );

    if (!matchingDurationTerms) return undefined;

    return matchingDurationTerms;
}

export function strategyIsTargetLoanCompatible(
    strategy: StrategyExpandedInfo | undefined,
    loanExpanded: AbfLoanExpanded | undefined,
    targetMint: string | undefined
): boolean {
    return !!getTargetStrategyMintDurationTerms(strategy, loanExpanded, targetMint);
}

export function getStrategyCollateralApyForDuration({
    strategy,
    collateralMint,
    currentLedgerDuration
}: {
    strategy: StrategyExpandedInfo | undefined;
    collateralMint: string | undefined;
    currentLedgerDuration: StrategyDuration | undefined;
}): number | undefined {
    if (!strategy || !collateralMint || !currentLedgerDuration) return undefined;

    const matchingDurationTerms = strategy.terms.assetTerms[collateralMint]?.durationAndApys.find(([duration]) =>
        isStrategyDurationSame(duration, currentLedgerDuration)
    );

    if (!matchingDurationTerms) return undefined;

    return matchingDurationTerms[1];
}
